import { axios } from 'api'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { camelToSnake } from 'utils'
import MembershipMetricsDoughnut from '../AWCMembershipMetrics/MembershipMetricsDoughnut'
import { AddButton } from '../ContentStructure/Components'
import moment from 'moment'

const Main = styled.div`
  .flex-box {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
  }
  .chart {
    margin: 0;
    max-width: 280px;
    width: fit-content;
    border: none;
  }
  .sub_items * {
    font-size: 95%;
  }
  .charts {
    display: flex;
    margin: 0 auto;
    width: fit-content;
    max-width: 100%;
    padding: 0;
    @media screen and (min-width: 1280px) {
      flex-direction: row;
      max-width: 50%;
    }
  }
  h4 {
    font-size: 160%;
    font-weight: 300;
  }
  .section {
    margin-top: 3rem;
  }

  ul {
    margin: 0 20px;
    li {
      font-size: 150%;
      line-height: 160%;
    }
  }
  .date-container {
    display: flex;
    align-items: center;
    gap: 3rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }
  .date-box {
    display: flex;
    flex-direction: column;
    .react-datepicker-wrapper {
      width: fit-content;
    }
    .error {
      font-size: 12px;
      height: 18px;
      color: red;
    }
  }
  .submit {
    min-width: 110px;
  }
  .left {
    display: flex;
    flex-direction: column;
    h4 {
      margin-top: 0;
    }
    max-width: none;
    @media screen and (min-width: 1280px) {
      max-width: 50%;
    }
  }
`

const listingData = [
  {
    key: 2,
    title: 'Refunds issued to users who chose refund',
    sub_items: [
      {
        title: 'Number of users',
        key: 'total_refund_by_user'
      },
      {
        title: 'Total Amount',
        key: 'total_refund_amount_by_user',
        amount: true
      }
    ]
  },
  {
    key: 3,
    title: 'Total gift vouchers issued to users who chose GV',
    sub_items: [
      {
        title: 'Number of users',
        key: 'total_gc_count'
      },
      {
        title: 'Total Amount',
        key: 'total_gc_amount',
        amount: true
      }
    ]
  },
  {
    key: 4,
    title: 'Total Refunds auto-issued',
    sub_items: [
      {
        title: 'Number of users',
        key: 'total_refund_by_auto_issue'
      },
      {
        title: 'Total Amount',
        key: 'total_refund_amount_by_auto_issue',
        amount: true
      }
    ]
  }
]

const MembershipDoughnutChart = ({ data }) =>
  data && (
    <div className='charts'>
      {Number(data?.total_gc_count) > 0 &&
        Number(data?.total_refund_count) > 0 && (
          <MembershipMetricsDoughnut
            data={[
              data?.total_refund_by_auto_issue,
              data?.total_refund_by_user,
              data?.total_gc_count
            ]}
            labels={['Auto Refund', 'User Refund', 'Vouchers']}
            title='Overall Count'
          />
        )}
      {Number(data?.total_refund_amount) > 0 &&
        Number(data?.total_gc_amount) > 0 && (
          <MembershipMetricsDoughnut
            data={[
              data?.total_refund_amount_by_auto_issue,
              data.total_refund_amount_by_user,
              data?.total_gc_amount
            ]}
            labels={['Auto Refund', 'User Refund', 'Vouchers']}
            title='Overall Amount'
            amount={true}
          />
        )}
    </div>
  )

const DateBox = ({ value, setValue, label, id, error = '' }) => (
  <div className='date-box'>
    <label htmlFor={id}>{label}</label>
    <DatePicker
      maxDate={new Date()}
      id={id}
      selected={value}
      onChange={(date) => setValue(date)}
    />
    <span className='error'>{error}</span>
  </div>
)

const today = new Date()
const oneMonthAgo = new Date()
oneMonthAgo.setMonth(today.getMonth() - 1)

const GCRefundMetricsEntry = () => {
  const [refundGCMetris, setRefundGCMetrics] = useState({})
  const [fromDate, setFromDate] = useState(oneMonthAgo)
  const [toDate, setToDate] = useState(today)
  const [loading, setLoading] = useState(false)

  const error = useMemo(
    () => moment(fromDate).isAfter(moment(toDate)),
    [fromDate, toDate]
  )

  const getData = useCallback(
    async ({ from = oneMonthAgo, to = today, first_load = false }) => {
      try {
        setLoading(true)
        const from_date = moment(from).toISOString()
        const to_date = moment(to).toISOString()
        const res = await axios.get('/gc_refund_metrics/get_data', {
          baseURL: '/admin',
          params: {
            from_date,
            to_date
          }
        })
        const data = res.data || {}
        const resData = camelToSnake(data)
        setRefundGCMetrics(resData)
      } catch (err) {
        console.error('Failed to fetch awc membership metric data:', err)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    getData({})
  }, [getData])

  const handleSubmit = () => !error && getData({ from: fromDate, to: toDate })

  if (!refundGCMetris || Object.keys(refundGCMetris).length === 0) return null

  return (
    <Main>
      <div className='date-container'>
        <DateBox
          value={fromDate}
          setValue={setFromDate}
          label='From Date (PST)'
          id='from'
          error={error && 'Dates are out of order'}
        />
        <DateBox
          value={toDate}
          setValue={setToDate}
          label='To Date (PST)'
          id='to'
        />
        <AddButton
          onClick={handleSubmit}
          type='button'
          className='btn submit btn-primary'
          disabled={loading}
        >
          {loading ? 'Fetching...' : 'Submit'}
        </AddButton>
      </div>
      <div className='flex-box'>
        <div className='left'>
          <ul>
            {listingData.map((item) => (
              <li key={item.key}>
                {item.title}
                {item?.sub_items?.length > 0 && (
                  <ul className='sub_items'>
                    {item?.sub_items.map((si) => (
                      <li key={si.key}>
                        {si.title}:{' '}
                        <b>
                          {si.amount ? '$' : ''}
                          {Number(refundGCMetris[si.key])}
                        </b>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li>
              Total Refund Amount (sum of all refund amounts):{' '}
              <b>${Number(refundGCMetris?.total_refund_amount)}</b>
            </li>
            <li>
              Total GV Amount (sum of all gift voucher amounts):{' '}
              <b>${Number(refundGCMetris?.total_gc_amount)}</b>
            </li>
          </ul>
        </div>
        <MembershipDoughnutChart data={refundGCMetris} />
      </div>
    </Main>
  )
}

export default GCRefundMetricsEntry
